/* eslint-disable class-methods-use-this */
import queryString from 'query-string';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { compose } from 'recompose';
import { AssessmentRS, OverallCommentFragment, TimeSpentRS } from 'models/api-response';
import { ELSModalServiceType } from 'models/els';
import { AssessmentStatus, AssignmentType, GradingOption, GradingResult, UserRole } from 'models/enum';
import { CaseStudy, CaseStudySubmissionSummary, ChartComment, NavigationItem, PatientContext, SimulationEhr } from 'models/ui';
import { Locales, RouteParams, RoutePath } from 'constants/app.constant';
import { apiHelper, appHelper, assignmentHelper } from 'helpers';
import { assignmentService, caseStudyService, chartService, simulationService } from 'services';
import { appActions, appSelectors } from 'redux/ducks/app';
import { ConfirmationModal, LoseDataWarning, withFormUtilities } from 'components/common';
import UnsavedChangesHandler from 'components/common/unsaved-changes-handler/UnsavedChangesHandler';
import AssessmentDetailView from './AssessmentDetailView';

interface MatchParam {
  assignmentId: string;
}

export interface StudentSubmission {
  studentId: number;
  assessmentId: number;
  displayName: string;
  status: AssessmentStatus;
}

export enum Tabs {
  CHART_ACTIVITY = 0,
  QUIZ_QUESTIONS = 1,
  SIMULATION_EHR_INFORMATION = 1,
  CASE_STUDY_INFORMATION = 2
}

const isSubmissionSummaryNotFound = (err: unknown): boolean => {
  const e = err as {
    response?: { status?: number; data?: { message?: string; status?: string } };
    message?: string;
  };
  const data = e?.response?.data;
  const message = String(data?.message ?? e?.message ?? '');
  const envelopeStatus = String(data?.status ?? '').toUpperCase();

  return e?.response?.status === 404 || envelopeStatus === 'NOT_FOUND' || /\b404\b/i.test(message) || /unable to find assessment/i.test(message);
};

export interface AssessmentDetailProps extends RouteComponentProps<MatchParam> {
  menuItems: NavigationItem[];
  modalService: ELSModalServiceType;
  getMenuItems: Function;
  showSaveSuccess: VoidFunction;
  fetchPatientContext: Function;
  locale: Locales;
  isInProgressGradingEnabledFlag: boolean;
  isInstructorSaveData: boolean;
  isInstructorSaveDataUS: boolean;
  userRole: string;
  loggedInUserId?: number;
  dataAssessmentInstructor: AssessmentRS | null;
  setDataAssessmentInstructor: (data: AssessmentRS | null) => void;
}

interface AssignmentDetailState {
  fragmentId: string;
  assignmentType: AssignmentType;
  assignmentGradeType: GradingOption;
  gradeStatus: GradingResult;
  score?: number;
  simChartId: string;
  assignmentId: number;
  assessmentId: number;
  selectedStudent: number;
  studentNavigationIndex: number;
  overallComment: string;
  overallCommentCreatedAt: string;
  isOverallCommentChanged: boolean;
  overallCommentInProgress: string;
  submissions: StudentSubmission[];
  title: string;
  startedAt: string;
  completedAt: string;
  durationTime: number;
  completedCharts: string[];
  instructorSetUpCharts: string[];
  instructorAssessmentId: number;
  status: string;
  chartComments: ChartComment[];
  instructorMenuItems: NavigationItem[];
  instructorSetUpChartComments: ChartComment[];
  activeTab: number;
  caseStudySubmissionSummary: CaseStudySubmissionSummary;
  selectedContent: CaseStudy | SimulationEhr;
  warningMessage: string;
  timeSpentData: TimeSpentRS[];
  isInProgressGradingEnabled: boolean;
}

export class AssessmentDetail extends Component<AssessmentDetailProps, AssignmentDetailState> {
  pastDueWarningMessage = 'Student assignment was not submitted.';
  warningUnsavedCommentModalId = 'loseDataModal';

  constructor(props) {
    super(props);
    this.state = {
      assignmentId: Number(this.props.match.params?.assignmentId),
      selectedStudent: this.getStudentFromPath(),
      fragmentId: '',
      assignmentType: null,
      assessmentId: null,
      instructorAssessmentId: null,
      assignmentGradeType: null,
      gradeStatus: null,
      score: null,
      simChartId: '',
      studentNavigationIndex: 0,
      overallComment: '',
      overallCommentCreatedAt: '',
      overallCommentInProgress: '',
      isOverallCommentChanged: false,
      submissions: [],
      title: '',
      startedAt: '',
      completedAt: '',
      durationTime: 0,
      completedCharts: [],
      instructorSetUpCharts: [],
      status: '',
      chartComments: [],
      instructorSetUpChartComments: [],
      instructorMenuItems: [],
      activeTab: Tabs.CHART_ACTIVITY,
      caseStudySubmissionSummary: null,
      selectedContent: null,
      warningMessage: '',
      timeSpentData: [],
      isInProgressGradingEnabled: false
    };
  }

  async componentDidMount() {
    this.loadAssignmentSummary();
    this.getTimeSpent();
    this.setState({ isInProgressGradingEnabled: this.props.isInProgressGradingEnabledFlag });
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedStudent, simChartId, assessmentId, submissions, instructorAssessmentId, instructorMenuItems } = this.state;
    const { menuItems } = this.props;

    if (!menuItems?.length) return;

    if (this.props.location?.search !== prevProps.location?.search) {
      this.handleRouteChange();
      return;
    }

    if (prevState.selectedStudent !== selectedStudent) {
      this.setState({ studentNavigationIndex: this.calculateStudentNavigationIndex(selectedStudent, submissions) });
      this.loadAssessmentDetail();
      this.loadAssessmentInstructor();
    }
    const isStudentMenu = assessmentId && menuItems?.some((mi) => mi.path?.includes(assessmentId.toString()));
    if (isStudentMenu && (prevState.simChartId !== simChartId || prevProps.menuItems !== menuItems) && simChartId) {
      chartService
        .buildChartComments(assessmentId?.toString(), simChartId, menuItems, this.state.completedCharts)
        .then((chartComments) => this.setState({ chartComments }))
        .catch((err) => apiHelper.showApiError(`can not load chart comments ${err}`));
    }

    const isInstructorMenu = instructorAssessmentId && instructorMenuItems?.some((mi) => mi.path?.includes(instructorAssessmentId.toString()));
    if (isInstructorMenu && (prevState.instructorSetUpCharts !== this.state.instructorSetUpCharts || prevState.instructorAssessmentId !== instructorAssessmentId) && simChartId) {
      chartService
        .buildChartComments(instructorAssessmentId?.toString(), simChartId, menuItems, this.state.instructorSetUpCharts)
        .then((instructorSetUpChartComments) => this.setState({ instructorSetUpChartComments }))
        .catch((err) => apiHelper.showApiError(`can not load instructor chart comments ${err}`));
    }
  }

  handleRouteChange = () => this.setState({ selectedStudent: this.getStudentFromPath() });

  getStudentFromPath = (): number => {
    const parsedQuery = queryString.parse(this.props.location.search);
    const { student } = parsedQuery;
    return Number(student);
  };

  getTimeSpent = () => {
    assignmentService.getTimeSpent(this.state.assignmentId).then((res) => {
      this.setState({ timeSpentData: res?.data });
    });
  };

  getNotSubmittedMsg(isInProgressFlagEnabled: boolean) {
    let notSubmittedWarningMessage = 'Student assignment is not yet submitted. Please check back later.';
    if (isInProgressFlagEnabled) {
      notSubmittedWarningMessage = 'Student assignment is not yet submitted.';
    }
    return notSubmittedWarningMessage;
  }

  calculateStudentNavigationIndex = (studentId: number, submissions: StudentSubmission[]): number => {
    const index = submissions.findIndex((item) => item.studentId === studentId);
    return index !== -1 ? index + 1 : 1;
  };

  loadAssignmentSummary = () =>
    appHelper.useLoader(
      assignmentService
        .getAssignmentSummary(this.state.assignmentId)
        .then(({ data }) => {
          const { title, assignmentType, assignmentGradeType } = data;
          const submissions: StudentSubmission[] = data.studentAssessmentSubmissions.map((item) => {
            const status = !assignmentHelper.isAssessmentSubmitted(item.status) ? '(incomplete)' : '';
            return {
              studentId: item.userId,
              assessmentId: item.assessmentId,
              displayName: `${item.firstName} ${item.lastName} ${status}`.trim(),
              status: item.status
            };
          });
          const studentNavigationIndex = this.calculateStudentNavigationIndex(this.state.selectedStudent, submissions);
          return new Promise((resolve) => this.setState({ submissions, title, assignmentType, assignmentGradeType, studentNavigationIndex }, () => resolve('')));
        })
        .then(this.loadAssessmentDetail)
        .then(this.loadAssessmentInstructor)
    );

  loadContent = (id: string, assignmentType: AssignmentType) => {
    if (assignmentType === AssignmentType.CASE_STUDY) {
      return caseStudyService.getCaseStudy(id).then(({ data }) => {
        this.setState({ selectedContent: data });
      });
    }
    if (assignmentType === AssignmentType.SIMULATION) {
      return simulationService.getSimulation(id).then(({ data }) => {
        this.setState({ selectedContent: data });
      });
    }
    return Promise.resolve();
  };

  bindAssessmentSubmissionSummary = (data) => {
    const { assessmentId, assignmentType, assignmentGradeType, gradeStatus, status, caseStudySubmissionSummary, simChartId } = data;
    const { startedAt, completedAt, durationTime, completedCharts } = data;
    const totalCorrect = data.caseStudySubmissionSummary?.totalCorrect;
    const totalQuestions = data.caseStudySubmissionSummary?.totalQuestions;
    const score = data.assignmentGradeType === GradingOption.SCORED ? assignmentHelper.getScore(totalCorrect, totalQuestions, status) : null;
    let warningMessage;
    switch (status) {
      case AssessmentStatus.NOT_STARTED:
      case AssessmentStatus.IN_PROGRESS:
        warningMessage = this.getNotSubmittedMsg(this.props.isInProgressGradingEnabledFlag);
        break;
      case AssessmentStatus.PAST_DUE:
        warningMessage = this.pastDueWarningMessage;
        break;
      default:
        warningMessage = '';
    }
    this.setState({
      score,
      gradeStatus,
      simChartId,
      assessmentId,
      status,
      startedAt,
      completedAt,
      durationTime,
      completedCharts: completedCharts || [],
      caseStudySubmissionSummary: {
        assignmentType,
        assignmentGradeType,
        ...caseStudySubmissionSummary
      },
      warningMessage
    });
  };

  applyMissingSubmissionSummaryState = () => {
    const assessmentStatus = this.state.submissions.find((item) => item.studentId === this.state.selectedStudent)?.status;
    this.setState(() => ({
      score: null,
      gradeStatus: null,
      simChartId: null,
      assessmentId: null,
      status: assessmentStatus,
      startedAt: null,
      completedAt: null,
      durationTime: 0,
      completedCharts: [],
      caseStudySubmissionSummary: null,
      warningMessage: assessmentStatus === AssessmentStatus.NOT_STARTED ? this.getNotSubmittedMsg(this.props.isInProgressGradingEnabledFlag) : this.pastDueWarningMessage
    }));
  };

  loadAssessmentDetail = () => {
    const { assignmentId, selectedStudent, submissions } = this.state;

    const hasAssessment = submissions.some((item) => item.studentId === selectedStudent && !!item.assessmentId);
    if (hasAssessment) {
      return appHelper.useLoader(
        assignmentService
          .getAssessmentSubmissionSummary(assignmentId, selectedStudent)
          .then(async ({ data }) => {
            this.bindAssessmentSubmissionSummary(data);
            const patientContext = await this.props.fetchPatientContext(data.assessmentId, data.assignmentType, true);

            const fetchAssessmentResultPromises = [this.props.getMenuItems(data.assessmentId, patientContext), this.loadOverallComment(data.simChartId)];
            if (data.assignmentType === AssignmentType.CASE_STUDY) {
              fetchAssessmentResultPromises.push(this.loadContent(data.caseStudySubmissionSummary.caseStudyId, data.assignmentType));
            } else if (data.assignmentType === AssignmentType.SIMULATION) {
              fetchAssessmentResultPromises.push(this.loadContent(data.simulationEhrSubmissionSummary?.simulationEhrId, data.assignmentType));
            }
            return Promise.all(fetchAssessmentResultPromises);
          })
          .catch((err) => {
            if (isSubmissionSummaryNotFound(err)) {
              this.applyMissingSubmissionSummaryState();
              return undefined;
            }
            throw err;
          }),
        { errorMessage: 'can not get assessment submission summary' }
      );
    }
    this.applyMissingSubmissionSummaryState();
    return Promise.resolve();
  };

  clearInstructorSetupState = () => {
    this.setState({
      instructorSetUpCharts: [],
      instructorAssessmentId: null,
      instructorMenuItems: [],
      instructorSetUpChartComments: []
    });
  };

  loadAssessmentInstructor = () => {
    if (this.state.assignmentType !== AssignmentType.EMPTY_EHR) {
      return Promise.resolve();
    }

    const { assignmentId } = this.state;
    const { isInstructorSaveData, isInstructorSaveDataUS, userRole, locale, loggedInUserId } = this.props;

    const isInstructorSaveDataEnabled = (isInstructorSaveData && locale === Locales.EN_AU) || (isInstructorSaveDataUS && locale === Locales.EN_US);
    const checkInstructorCase = isInstructorSaveDataEnabled && userRole === UserRole.INSTRUCTOR;
    const instructorId = checkInstructorCase ? loggedInUserId : null;

    if (instructorId) {
      return appHelper.useLoader(
        assignmentService
          .getAssessmentSubmissionSummary(assignmentId, instructorId)
          .then(async ({ data }) => {
            const patientInsContext = await this.props.fetchPatientContext(data.assessmentId, data.assignmentType, true);
            const menuItems = await this.props.getMenuItems(data.assessmentId, patientInsContext);
            this.setState({
              instructorSetUpCharts: data.completedCharts || [],
              instructorAssessmentId: data.assessmentId,
              instructorMenuItems: menuItems?.payload
            });
          })
          .catch((err) => {
            if (isSubmissionSummaryNotFound(err)) {
              this.clearInstructorSetupState();
              return undefined;
            }
            throw err;
          }),
        { errorMessage: 'can not get assessment Instructor set up' }
      );
    }

    return Promise.resolve();
  };

  loadOverallComment = (chartId: string) =>
    chartService
      .getOverallComment(chartId)
      .then((commentFragment: OverallCommentFragment) =>
        this.setState({ fragmentId: commentFragment?.fragmentId || '', overallComment: commentFragment?.chartData?.comment, overallCommentCreatedAt: commentFragment?.createdAt })
      );

  handleStudentChange = (event) => {
    const studentId = event.target.value;
    if (!this.state.isOverallCommentChanged) {
      this.props.history.push({
        pathname: this.props.location.pathname,
        search: `?student=${studentId}`
      });
    } else {
      const { modalService } = this.props;
      modalService.openModal({
        modalId: this.warningUnsavedCommentModalId,
        content: (
          <ConfirmationModal
            header="Before you Leave..."
            message={ConfirmationModal.UNSAVED_MESSAGE}
            onOkClick={() => {
              this.handleSaveClick().then(() => {
                modalService.closeModal(this.warningUnsavedCommentModalId);
                this.props.history.push({
                  pathname: this.props.location.pathname,
                  search: `?student=${studentId}`
                });
              });
            }}
            onCancelClick={() => modalService.closeModal(this.warningUnsavedCommentModalId)}
          />
        )
      });
    }
  };

  handleOverallCommentChange = (event) => this.setState({ overallComment: event.target.value, isOverallCommentChanged: true, overallCommentInProgress: event.target.value });

  handleBackToDetail = () => {
    const { assignmentId } = this.props.match.params;
    this.props.history.push({
      pathname: RoutePath.instructor.grade.replace(RouteParams.ASSIGNMENT_ID, assignmentId)
    });
  };

  handleSaveClick = () => {
    const { simChartId, fragmentId, overallComment } = this.state;
    return appHelper.useLoader(
      chartService
        .saveOverallComment(simChartId, fragmentId, overallComment)
        .then(() =>
          this.setState({
            isOverallCommentChanged: false,
            overallCommentInProgress: '',
            overallCommentCreatedAt: new Date().toISOString()
          })
        )
        .then(this.props.showSaveSuccess),
      { errorMessage: 'can not save comment' }
    );
  };

  handleViewChart = () => {
    const { chartComments, instructorSetUpChartComments } = this.state;
    const firstChart = [...chartComments, ...instructorSetUpChartComments].find((chart) => !!chart.path);
    if (firstChart) {
      this.props.history.push(firstChart.path);
    }
  };

  handleViewSpecificChart = (path: string) => this.props.history.push(path);

  handleTabGroupClick = (tabIndex) => this.setState({ activeTab: tabIndex });

  updatePathWithStudent = (studentId: number): void =>
    this.props.history.push({
      pathname: this.props.location.pathname,
      search: `?student=${studentId}`
    });

  getCurrentSubmissionIndex = (): number => this.state.submissions.findIndex((item) => item.studentId === this.state.selectedStudent);

  handleNavigationAttempt = () =>
    new Promise((resolve) => {
      const { modalService } = this.props;
      if (!this.state.isOverallCommentChanged) {
        resolve(true);
      } else {
        modalService.openModal({
          modalId: this.warningUnsavedCommentModalId,
          content: (
            <LoseDataWarning
              onLeaveClick={() => {
                modalService.closeModal(this.warningUnsavedCommentModalId);
                resolve(true);
              }}
              onStayClick={() => {
                modalService.closeModal(this.warningUnsavedCommentModalId);
                resolve(false);
              }}
            />
          )
        });
      }
    });

  handlePrevStudentClick = () => {
    if (!this.state.isOverallCommentChanged) {
      this.updatePathWithStudent(this.state.submissions[this.getCurrentSubmissionIndex() - 1].studentId);
    } else {
      const { modalService } = this.props;
      modalService.openModal({
        modalId: this.warningUnsavedCommentModalId,
        content: (
          <ConfirmationModal
            header="Before you Leave..."
            message={ConfirmationModal.UNSAVED_MESSAGE}
            onOkClick={() => {
              this.handleSaveClick().then(() => {
                modalService.closeModal(this.warningUnsavedCommentModalId);
                this.updatePathWithStudent(this.state.submissions[this.getCurrentSubmissionIndex() - 1].studentId);
              });
            }}
            onCancelClick={() => modalService.closeModal(this.warningUnsavedCommentModalId)}
          />
        )
      });
    }
  };

  handleNextStudentClick = () => {
    if (!this.state.isOverallCommentChanged) {
      this.updatePathWithStudent(this.state.submissions[this.getCurrentSubmissionIndex() + 1].studentId);
    } else {
      const { modalService } = this.props;
      modalService.openModal({
        modalId: this.warningUnsavedCommentModalId,
        content: (
          <ConfirmationModal
            header="Before you Leave..."
            message={ConfirmationModal.UNSAVED_MESSAGE}
            onOkClick={() => {
              this.handleSaveClick().then(() => {
                modalService.closeModal(this.warningUnsavedCommentModalId);
                this.updatePathWithStudent(this.state.submissions[this.getCurrentSubmissionIndex() + 1].studentId);
              });
            }}
            onCancelClick={() => modalService.closeModal(this.warningUnsavedCommentModalId)}
          />
        )
      });
    }
  };

  render() {
    const {
      title,
      assignmentType,
      assignmentGradeType,
      gradeStatus,
      selectedContent,
      submissions,
      caseStudySubmissionSummary,
      assignmentId,
      assessmentId,
      instructorAssessmentId,
      startedAt,
      completedAt,
      durationTime,
      status,
      score,
      completedCharts,
      chartComments,
      instructorSetUpChartComments,
      overallComment,
      overallCommentCreatedAt,
      overallCommentInProgress,
      timeSpentData,
      activeTab,
      selectedStudent,
      studentNavigationIndex,
      warningMessage,
      isInProgressGradingEnabled
    } = this.state;
    const { locale } = this.props;

    const viewProps = {
      title,
      assignmentType,
      assignmentGradeType,
      submissions,
      startedAt,
      completedAt,
      durationTime,
      completedCharts,
      score,
      status,
      gradeStatus,
      selectedStudent,
      chartComments,
      instructorSetUpChartComments,
      overallComment,
      overallCommentCreatedAt,
      overallCommentInProgress,
      selectedContent,
      caseStudySubmissionSummary,
      isInProgressGradingEnabled,
      questions: caseStudySubmissionSummary?.sequenceChunkSummaries
        ?.flatMap((chunk) => chunk.stepChunks)
        .map((step) => ({
          ...step.content,
          correct: step.correct,
          selectedAnswers: step.selectedAnswers
        })),
      activeTab,
      studentNavigationIndex,
      warningMessage,
      onBackToDetail: this.handleBackToDetail,
      onViewChart: this.handleViewChart,
      onStudentChange: this.handleStudentChange,
      onTabsClick: this.handleTabGroupClick,
      onViewSpecificChart: this.handleViewSpecificChart,
      onOverallCommentChange: this.handleOverallCommentChange,
      onSaveClick: this.handleSaveClick,
      onPrevStudentClick: this.handlePrevStudentClick,
      onNextStudentClick: this.handleNextStudentClick,
      timeSpentData: timeSpentData.find((timeSpent) => timeSpent.assessmentId === assessmentId),
      assignmentId,
      assessmentId,
      instructorAssessmentId,
      locale
    };

    return (
      <>
        <AssessmentDetailView {...viewProps} />
        <UnsavedChangesHandler handler={this.handleNavigationAttempt} hasUnsavedChanges={() => this.state.isOverallCommentChanged} />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  menuItems: appSelectors.getMenuItems(state),
  locale: appSelectors.getLocale(state),
  isInProgressGradingEnabledFlag: appSelectors.getIsInProgressGradingEnabledFlag(state),
  isInstructorSaveData: appSelectors.getInstructorSaveData(state),
  isInstructorSaveDataUS: appSelectors.getInstructorSaveDataUS(state),
  userRole: appSelectors.getUserRole(state),
  loggedInUserId: appSelectors.getUserId(state),
  dataAssessmentInstructor: appSelectors.getDataInstructorDetail(state)
});

const mapDispatchToProps = (dispatch) => ({
  getMenuItems: (assessmentId: number, patientContext: PatientContext) => dispatch(appActions.fetchMenuItems(assessmentId, patientContext)),
  fetchPatientContext: (simChartAssessmentId, assignmentType: AssignmentType, skipCache: boolean) =>
    dispatch(appActions.fetchPatientContext(simChartAssessmentId, assignmentType, skipCache)),
  setDataAssessmentInstructor: (data: (AssessmentRS & { userId: string }) | null) => dispatch(appActions.setDataAssessmentInstructor(data))
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withFormUtilities, withRouter)(AssessmentDetail);
